"use client";

import NextLink from "next/link";
import { Divider, Text } from "@stone-payments/jade";
import { LogoCatalog } from "@/components/logo-catalog";
import { useStore } from "@/contexts/store";
import { Brand } from "@/types/general";
import { Mask } from "@/utils/masks";
import { Skeleton } from "@/components/skeleton";
import { Container } from "@/components/container";
import { FooterInformation } from "@/components/footer/information";
import { BrandSymbol } from "@/components/brand-symbol";

type Props = {
  brand: Brand;
  loading?: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const RegulatoryLinks = () => (
  <div className="flex items-center gap-2">
    <NextLink href="/politica-de-privacidade">
      <Text as="span" className="underline" color="low">
        Política de privacidade
      </Text>
    </NextLink>
    <Text color="low">•</Text>
    <NextLink href="/termos-de-uso">
      <Text as="span" className="underline" color="low">
        Termos de uso
      </Text>
    </NextLink>
  </div>
);

export const FooterLegal = ({ brand }: Pick<Props, "brand">) => {
  return (
    <div className="flex w-full flex-col items-center gap-2">
      <LogoCatalog brand={brand} data-testid="footer-logo" />

      {/* This component will be used in the future with the implementation of customization */}
      {/* <RegulatoryLinks /> */}
    </div>
  );
};

export const Footer = ({ brand, loading = false }: Props) => {
  const { store } = useStore();

  return (
    <footer className="mt-12 mb-5 flex w-full flex-col items-center gap-5 xl:mb-8">
      <div className="w-full lg:pb-1">
        <Divider />
      </div>

      <div className="flex w-full flex-col items-center gap-1">
        {loading ? (
          <>
            <Skeleton
              width="w-16"
              height="h-16"
              className="-mt-14 mb-1 !rounded-full"
            />

            <Skeleton width="w-24" height="h-5" />

            <Skeleton width="w-42" height="h-5" />
          </>
        ) : (
          <>
            {store.vitrine.brandLogo?.url && (
              <figure className="-mt-14 mb-1">
                <BrandSymbol
                  img={store.vitrine.brandLogo.url}
                  alt={`${store.name} logo`}
                  size="large"
                  loading={loading}
                  border
                />
              </figure>
            )}

            <Text variant="heading-xsmall" data-testid="footer-store-name">
              {store.name}
            </Text>

            {Mask.documentType(store.document) === "CNPJ" ? (
              <Text
                variant="text-small"
                color="medium"
                data-testid="footer-store-document"
              >
                {Mask.formatDocument(store.document, true)}
              </Text>
            ) : null}
          </>
        )}
      </div>

      <FooterInformation store={store} loading={loading} />

      <Container>
        <Divider />
      </Container>

      <FooterLegal brand={brand} />
    </footer>
  );
};
